// src/components/VendorRfqResponse.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const commonInputClass = "mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 bg-gray-100";
const commonLabelClass = "block text-sm font-medium text-gray-700";

const VendorRfqResponse = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [rfq, setRfq] = useState(null);
    const [formData, setFormData] = useState({
        considerations: '',
        vendor_timeline: '',
        other_costs: '',
        overall_cost: '',
        // The general 'alternative_options' field on the form can be for truly global alternatives
        // if method-specific ones are handled separately.
        alternative_options_general: '',
    });
    const [feasibility, setFeasibility] = useState({});
    const [cpi, setCpi] = useState({});
    const [alternativeOptionsPerMethod, setAlternativeOptionsPerMethod] = useState({}); // For method-specific alternatives
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchRfq = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await api.get(`/rfqs/public/invitation/${token}/`);
                console.log("API Response Data:", response.data);

                if (!response.data) {
                    setError("Failed to load RFQ details: No data received.");
                    setRfq(null);
                    setIsLoading(false);
                    return;
                }
                setRfq(response.data);

                const initialFeasibility = {};
                const initialCpi = {};
                const initialAlternativesPerMethod = {};

                if (response.data.data_collection_methods && Array.isArray(response.data.data_collection_methods)) {
                    response.data.data_collection_methods.forEach(method => {
                        initialFeasibility[method] = 'Feasible';
                        initialCpi[method] = '';
                        initialAlternativesPerMethod[method] = '';
                    });
                } else {
                    console.error("data_collection_methods is missing or not an array in API response.");
                }
                setFeasibility(initialFeasibility);
                setCpi(initialCpi);
                setAlternativeOptionsPerMethod(initialAlternativesPerMethod);

            } catch (err) {
                console.error("Error fetching RFQ:", err);
                let msg = "Invalid or expired invitation link.";
                if (err.response?.data?.error) msg = err.response.data.error;
                else if (err.response?.status) msg = `Error ${err.response.status}: Failed to load invitation details.`;
                else if (err.request) msg = "Network error or server did not respond.";
                setError(msg);
                setRfq(null);
            } finally {
                setIsLoading(false);
            }
        };

        if (token) {
            fetchRfq();
        } else {
            setError("Invalid invitation link: No token provided.");
            setIsLoading(false);
        }
    }, [token]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFeasibilityChange = (method, value) => {
        setFeasibility(prev => ({ ...prev, [method]: value }));
        if (value === 'Feasible') {
            setAlternativeOptionsPerMethod(prev => ({ ...prev, [method]: '' }));
        } else {
            setCpi(prev => ({ ...prev, [method]: '' })); // Clear CPI if not feasible
        }
    };

    const handleAlternativeOptionChange = (method, value) => {
        setAlternativeOptionsPerMethod(prev => ({ ...prev, [method]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        if (!formData.overall_cost || parseFloat(formData.overall_cost) <= 0) {
            alert("Overall Project Cost is required and must be greater than 0.");
            setIsSubmitting(false);
            return;
        }
        
        // Filter out CPIs for methods marked as "Not feasible" or empty CPIs for feasible methods if desired
        const cpi_responses_payload = {};
        for (const method in cpi) {
            if (feasibility[method] === 'Feasible' && cpi[method] && cpi[method].trim() !== '') {
                cpi_responses_payload[method] = parseFloat(cpi[method]);
            } else if (feasibility[method] === 'Feasible' && (!cpi[method] || cpi[method].trim() === '')) {
                // Handle case where method is feasible but CPI is blank - depends on backend validation
                // For now, let's include it as null or expect backend to validate
                // Or you could raise an alert here.
                alert(`CPI for ${method} is required as it's marked feasible.`);
                setIsSubmitting(false);
                return;
            }
        }


        const payload = {
            considerations: formData.considerations,
            vendor_timeline: formData.vendor_timeline,
            other_costs: formData.other_costs ? parseFloat(formData.other_costs) : null,
            overall_cost: parseFloat(formData.overall_cost),
            alternative_options: formData.alternative_options_general, // This is for the general field
            feasibility_responses: feasibility,
            cpi_responses: cpi_responses_payload,
            method_specific_alternatives: alternativeOptionsPerMethod, // Sending structured alternatives
        };
        console.log("Submitting payload:", payload);

        try {
            await api.post(`/rfqs/public/invitation/${token}/submit/`, payload);
            alert("Quotation submitted successfully. Thank you!");
            navigate('/thank-you');
        } catch (err) {
            console.error("Submission error:", err.response || err);
            let errorMessage = "Failed to submit quotation.";
            if (err.response && err.response.data) {
                const backendErrors = err.response.data;
                errorMessage += "\nDetails:\n";
                if (typeof backendErrors === 'string') {
                    errorMessage += backendErrors;
                } else if (typeof backendErrors === 'object') {
                    for (const key in backendErrors) {
                        // Check if backendErrors[key] is an array before joining
                        const errorValue = Array.isArray(backendErrors[key]) ? backendErrors[key].join(', ') : backendErrors[key];
                        errorMessage += `${key.replace(/_/g, ' ')}: ${errorValue}\n`;
                    }
                }
            }
            setError(errorMessage.trim());
            alert(errorMessage.trim());
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) return <div className="text-center p-10 text-xl">Loading Request Details...</div>;
    if (error && !rfq) return <div className="text-center p-10 text-red-600 font-bold text-xl">{error}</div>;
    if (!rfq) return <div className="text-center p-10 text-gray-600">Could not load RFQ details. Please try again or contact support.</div>;

    const currency = rfq.countries && rfq.countries.includes('UAE') ? 'AED' : 'USD';

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <h1 className="text-3xl font-extrabold text-gray-900 text-center">Quotation Request</h1>
                <p className="text-center text-gray-600 mt-2 mb-6">Please review the project details and submit your quotation below.</p>

                {error && <div className="my-4 p-4 bg-red-100 text-red-700 border border-red-400 rounded-md whitespace-pre-wrap">{error}</div>}

                <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200">
                    <h2 className="text-xl font-bold text-gray-800">Project Summary</h2>
                    <dl className="mt-4 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                        <div><dt className="font-medium text-gray-500">RFQ Name</dt><dd className="text-gray-900">{rfq.name || 'N/A'}</dd></div>
                        <div><dt className="font-medium text-gray-500">Countries</dt><dd className="text-gray-900">{rfq.countries?.join(', ') || 'N/A'}</dd></div>
                        <div><dt className="font-medium text-gray-500">Methodologies (Overall)</dt><dd className="text-gray-900">{rfq.methodologies?.join(', ') || 'N/A'}</dd></div>
                        <div className="sm:col-span-2"><dt className="font-medium text-gray-500">Target Segment</dt><dd className="text-gray-900 whitespace-pre-wrap">{rfq.target_segment || 'N/A'}</dd></div>
                        <div className="sm:col-span-2"><dt className="font-medium text-gray-500">Recruitment Criteria</dt><dd className="text-gray-900 whitespace-pre-wrap">{rfq.recruitment_criteria || 'N/A'}</dd></div>
                    </dl>
                </div>

                <form onSubmit={handleSubmit} className="mt-8 bg-white shadow-lg rounded-lg p-6 space-y-6 border border-gray-200">
                    <h2 className="text-xl font-bold text-gray-800">Your Quotation Details</h2>

                    {rfq.data_collection_methods && rfq.data_collection_methods.length > 0 ? (
                        rfq.data_collection_methods.map(method => (
                            <div key={method} className="p-4 border rounded-md bg-gray-50 space-y-4">
                                <h3 className="font-semibold text-lg text-indigo-700">{method}</h3>
                                <p className="text-sm text-gray-700">Required Sample: <span className="font-medium">{rfq.sample_sizes && rfq.sample_sizes[method] ? rfq.sample_sizes[method] : 'N/A'}</span></p>
                                
                                <div>
                                    <label htmlFor={`feasibility-${method}`} className={commonLabelClass}>Feasibility for <span className="font-semibold">{method}</span></label>
                                    <select id={`feasibility-${method}`} value={feasibility[method] || 'Feasible'} onChange={e => handleFeasibilityChange(method, e.target.value)} className={commonInputClass}>
                                        <option value="Feasible">Feasible</option>
                                        <option value="Not feasible">Not feasible</option>
                                    </select>
                                </div>

                                {feasibility[method] === 'Not feasible' && (
                                    <div>
                                        <label htmlFor={`alternative-${method}`} className={`${commonLabelClass} mt-1 text-orange-700`}>Alternative Option / Sample Size for <span className="font-semibold">{method}</span></label>
                                        <textarea
                                            id={`alternative-${method}`}
                                            rows="2"
                                            className={commonInputClass + " border-orange-300 focus:border-orange-500 focus:ring-orange-500"}
                                            value={alternativeOptionsPerMethod[method] || ''}
                                            onChange={e => handleAlternativeOptionChange(method, e.target.value)}
                                            placeholder={`e.g., Feasible with sample size X, or suggest new approach`}
                                        />
                                    </div>
                                )}

                                <div>
                                    <label htmlFor={`cpi-${method}`} className={`${commonLabelClass} ${feasibility[method] === 'Not feasible' ? 'text-gray-400' : ''}`}>
                                        Cost Per Interview (CPI) for <span className="font-semibold">{method}</span> ({currency})
                                        {feasibility[method] === 'Not feasible' && <span className="text-xs"> (N/A)</span>}
                                    </label>
                                    <input
                                        type="number"
                                        id={`cpi-${method}`}
                                        step="0.01" min="0"
                                        value={cpi[method] || ''}
                                        onChange={e => setCpi(prev => ({...prev, [method]: e.target.value}))}
                                        className={`${commonInputClass} ${feasibility[method] === 'Not feasible' ? 'bg-gray-200 cursor-not-allowed' : ''}`}
                                        placeholder={feasibility[method] === 'Not feasible' ? "N/A" : "0.00"}
                                        disabled={feasibility[method] === 'Not feasible'}
                                    />
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-gray-600 text-sm p-4 border rounded-md bg-gray-50">No specific data collection methods listed by the client for individual pricing. Please provide overall costs and considerations, or use the general alternative options field if proposing specific methods.</p>
                    )}
                    
                    <div>
                        <label htmlFor="alternative_options_general" className={commonLabelClass}>
                            General Alternative Options / Strategies (if any, not covered above)
                        </label>
                        <textarea 
                            id="alternative_options_general" 
                            name="alternative_options_general" 
                            rows="3" 
                            className={commonInputClass} 
                            value={formData.alternative_options_general} 
                            onChange={handleInputChange}
                            placeholder="General notes on alternative approaches or if proposing methods not listed by client."
                        />
                    </div>
                     <div>
                        <label htmlFor="considerations" className={commonLabelClass}>General Considerations / Notes</label>
                        <textarea id="considerations" name="considerations" rows="3" className={commonInputClass} value={formData.considerations} onChange={handleInputChange}></textarea>
                    </div>
                     <div>
                        <label htmlFor="vendor_timeline" className={commonLabelClass}>Proposed Project Timeline</label>
                        <input type="text" id="vendor_timeline" name="vendor_timeline" className={commonInputClass} value={formData.vendor_timeline} onChange={handleInputChange} placeholder="e.g., X working days from approval"/>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="other_costs" className={commonLabelClass}>Other Costs ({currency}) (e.g., incentives, travel)</label>
                            <input type="number" step="0.01" min="0" id="other_costs" name="other_costs" className={commonInputClass} value={formData.other_costs} onChange={handleInputChange} placeholder="0.00"/>
                        </div>
                         <div>
                            <label htmlFor="overall_cost" className={commonLabelClass}>Overall Project Cost ({currency})</label>
                            <input type="number" step="0.01" min="0.01" id="overall_cost" name="overall_cost" className={commonInputClass} value={formData.overall_cost} onChange={handleInputChange} placeholder="0.00" required/>
                        </div>
                    </div>

                    <div className="pt-5 flex justify-end">
                        <button type="submit" disabled={isSubmitting || isLoading} className="py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
                            {isSubmitting ? 'Submitting...' : 'Submit Quotation'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default VendorRfqResponse;